import type React from 'react';

import { Logo, Text } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { ProofItem } from '#src/app/pages/loginOrSignup/shared/components';
import { useProofCycle } from '#src/app/pages/loginOrSignup/shared/hooks';

const PROOF_CARD_CYCLE_MS = 3600;
const PROOF_ITEM_COUNT = 3;

/**
 * Desktop-only promo panel — the left column of the V3d SAP account wall.
 *
 * It lives in the layout rather than in a step, so it persists across steps and its cycle is never
 * remounted by a step change.
 */
export const PromoPanel: React.FC<EmptyProps> = () => {
	const { translate } = useI18n();

	return (
		<div
			data-qa="sap-cycling-proof-promo-panel"
			data-testid="promo-panel"
			// no rounded corners: the panel is full-bleed, so its left edge is the viewport's
			className="text-typography-inverse-strong-fixed hidden flex-col bg-[linear-gradient(155deg,#2a4fd6,#12307f)] p-12 md:flex"
		>
			<Logo variant="symbol" size="large" color="primary" ariaLabel={translate('corgi.components.logo.change_org')} />

			{/* capped at the design's 400px and vertically centred between the logo and the proof card */}
			<div className="flex max-w-100 flex-1 flex-col justify-center gap-4 py-8">
				<Text as="h2" variant="heading" size="medium" fontWeight="bold">
					<Translate value="corgi.login_or_signup.sap_cycling_proof.promo.title" />
				</Text>
				<Text as="p" variant="body" size="medium" className="text-white/80">
					<Translate value="corgi.login_or_signup.sap_cycling_proof.promo.subtitle" />
				</Text>
			</div>

			<ProofCard />
		</div>
	);
};

const ProofCard: React.FC<EmptyProps> = () => {
	const {
		data: { activeIndex },
	} = useProofCycle({ intervalMs: PROOF_CARD_CYCLE_MS, itemCount: PROOF_ITEM_COUNT });

	return (
		<div
			data-qa="sap-cycling-proof-card"
			data-testid="proof-card"
			// min-h reserves the tallest item's height so a swap never shifts the panel
			className="rounded-large flex min-h-13 items-center justify-center border border-white/20 bg-white/10 px-4 py-3 text-center"
		>
			<ProofItem index={activeIndex} iconSize={18} textSize="small" />
		</div>
	);
};
